/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { IssueAppEntryPointEntrypointProps } from '@jira/issue__issue-view/src/IssueAppEntryPoint.entrypoint.types.tsx';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { fg } from '@atlassian/jira-feature-gating';

const EMPTY_OBJECT = {} as const;

type IssueAppEntryPointComponent = EntryPointComponent<
	{},
	{},
	IssueAppEntryPointEntrypointProps,
	{}
>;

/**
 * NIN-specific Issue App entry point (shared by the global and project issue
 * navigators).
 *
 * This is a functionally-identical copy of the shared `issueAppEntryPoint`
 * (`@jira/issue__issue-view/src/issueAppEntryPoint.entrypoint.tsx`). It resolves
 * the exact same `IssueAppEntrypoint.tsx` root module (the JSResource cache keys
 * by module id, so no extra bundle weight and the same component renders), but is
 * a *distinct entry-point object*.
 *
 * Why this exists (EM-10565):
 * When `nin_glance_panel_technical_changes` is enabled, the NIN page entry point
 * nests the same shared `issueAppEntryPoint` singleton via two paths — the
 * main-content branch (the issue navigator) and the preview-panel branch
 * (`issuePreviewPanelEntryPoint`). During SSR, `loadEntryPointDataThenModules`
 * swaps every entry point's `root` to an empty-fragment dummy and restores it
 * afterwards, but `forAllEntryPoints` does not dedupe by object identity. The
 * shared singleton is therefore visited twice: the second swap captures the
 * already-swapped dummy as the "original", and the restore leaves the singleton
 * permanently pointing at the empty fragment. The Issue App then renders empty on
 * the server for NIN (but hydrates fine on the client).
 *
 * Using a distinct entry-point object for the NIN main-content branch breaks the
 * shared-identity collision so both branches restore correctly. The preview panel
 * keeps using the shared `issueAppEntryPoint` unchanged. Both navigators use this
 * single object, gated behind `nin_issue_app_conditional_entrypoint_ssr`.
 *
 * This is a workaround. The proper fix is to make the platform swap/restore in
 * `loadEntryPointDataThenModules` idempotent for shared entry points; once that
 * lands this file can be removed and the shared `issueAppEntryPoint` used directly.
 */
export const ninIssueAppEntryPoint: EntryPoint<IssueAppEntryPointComponent, {}> = createEntryPoint({
	root: JSResourceForUserVisible<IssueAppEntryPointComponent>(
		/* webpackChunkName: "async-issue-app-entrypoint" */ '@jira/issue__issue-view/src/IssueAppEntrypoint.tsx',
		{ entryFsSsrLiftImportToModule: true },
	),
	getPreloadProps: () => (fg('nike-react-compiler-nin-transition-fixes') ? EMPTY_OBJECT : {}),
});
